import { Component, computed, ElementRef, HostListener, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { AppKitService } from '../../services/app-kit.service';
import { LocalStorage } from '../../services/local-storage.service';
import { LayoutService } from '../../../core/services/layout.service';
import { ClipboardService } from '../../services/clipboard.service';
import { ToastrService } from '../../services/toastr.service';
import { finalize } from 'rxjs';
import { ProjectRole } from '../../enums/common.enum';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css'
})
export class NavbarComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly appKitService = inject(AppKitService);
  private readonly localStorageService = inject(LocalStorage);
  public readonly layoutService = inject(LayoutService);
  private readonly clipboardService = inject(ClipboardService);
  private readonly toastrService = inject(ToastrService);
  private readonly elementRef = inject(ElementRef);

  isMenuOpen = this.layoutService.isMobileSidebarOpen;
  connectedWalletAddress = signal('');
  isSubAdmin = signal(false);
  profileName = signal('Admin');
  draftProfileName = signal('');
  profileEmail = signal('');
  isProfileOpen = signal(false);
  isEditingProfile = signal(false);
  isSavingProfileName = signal(false);
  profileRoleLabel = computed(() => (this.isSubAdmin() ? ProjectRole.SUB_ADMIN : ProjectRole.ADMIN));
  profileInitials = computed(() => this.getInitials(this.profileName()));
  draftProfileInitials = computed(() => this.getInitials(this.draftProfileName()));

  ngOnInit(): void {
    const { loginResponse, subAdminDetails, savedSubAdminProfileName, savedAdminProfileName } =
    this.loadProfileStorage();
    this.applyRoleContext(loginResponse, subAdminDetails);
    this.initializeProfileName(loginResponse, subAdminDetails, savedSubAdminProfileName, savedAdminProfileName);
    this.initializeProfileEmail(loginResponse, subAdminDetails);
  }

  /**
   * Retrieves the stored profile data from local storage.
   *
   * @returns an object containing the login response, sub-admin details, saved sub-admin profile name, and saved admin profile name.
   */
  private loadProfileStorage() {
    return {
      loginResponse: this.localStorageService.getDataByKey('loginResponse') as any,
      subAdminDetails: this.localStorageService.getDataByKey('subAdminDetails'),
      savedSubAdminProfileName: this.localStorageService.getDataByKey('subAdminProfileName'),
      savedAdminProfileName: this.localStorageService.getDataByKey('adminProfileName')
    };
  }

  /**
   * Applies the role context based on the login response and sub-admin details.
   *
   * If the login response contains a wallet address, it sets the connected wallet address and sets isSubAdmin to false.
   * If the sub-admin details are present, it sets the connected wallet address to an empty string and sets isSubAdmin to true.
   *
   * @param loginResponse - The response from the login API call.
   * @param subAdminDetails - The sub-admin details.
   */
  private applyRoleContext(loginResponse: any, subAdminDetails: any) {
    if (subAdminDetails) {
      this.connectedWalletAddress.set('');
      this.isSubAdmin.set(true);
    } else if (loginResponse?.walletAddress) {
      this.connectedWalletAddress.set(loginResponse.walletAddress);
      this.isSubAdmin.set(false);
    }
  }

/**
 * Initializes the profile name based on the login response and sub-admin details.
 *
 * If the user is a sub-admin, it sets the profile name to the sub-admin's name if it exists, otherwise it sets it to the saved sub-admin profile name if it exists, otherwise it sets it to 'Sub Admin'.
 * If the user is not a sub-admin, it sets the profile name to the admin's name if it exists, otherwise it sets it to the saved admin profile name if it exists, otherwise it sets it to 'Admin'.
 *
 * @param loginResponse - The response from the login API call.
 * @param subAdminDetails - The sub-admin details.
 * @param savedSubAdminProfileName - The saved sub-admin profile name from local storage.
 * @param savedAdminProfileName - The saved admin profile name from local storage.
 */
  private initializeProfileName(
    loginResponse: any,
    subAdminDetails: any,
    savedSubAdminProfileName: any,
    savedAdminProfileName: any
  ) {
    if (this.isSubAdmin()) {
      const apiName = (subAdminDetails as any)?.name;
      const preferredName = typeof apiName === 'string' && apiName.trim()
        ? apiName.trim()
        : (typeof savedSubAdminProfileName === 'string' && savedSubAdminProfileName.trim()
          ? savedSubAdminProfileName.trim()
          : 'Sub Admin');
      this.profileName.set(preferredName);
      this.localStorageService.storeData('subAdminProfileName', preferredName);
      return;
    }

    const apiName = loginResponse?.name ?? loginResponse?.admin?.name;
    const preferredName = typeof apiName === 'string' && apiName.trim()
      ? apiName.trim()
      : (typeof savedAdminProfileName === 'string' && savedAdminProfileName.trim()
        ? savedAdminProfileName.trim()
        : 'Admin');
    this.profileName.set(preferredName);
    this.localStorageService.storeData('adminProfileName', preferredName);
  }

  private initializeProfileEmail(loginResponse: any, subAdminDetails: any) {
    const rawEmail = this.isSubAdmin()
      ? ((subAdminDetails as any)?.email ?? (subAdminDetails as any)?.user?.email)
      : (loginResponse?.email ?? loginResponse?.admin?.email);

    const email = typeof rawEmail === 'string' ? rawEmail.trim() : '';
    this.profileEmail.set(email);
  }

  /**
   * Derives initials from a profile name, falling back to role-based initials if empty.
   *
   * @param name - The profile name.
   * @returns the derived initials.
   */
  private getInitials(name: string): string {
    const trimmed = name.trim();
    if (!trimmed) return this.isSubAdmin() ? 'SA' : 'AD';
    const parts = trimmed.split(' ').filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  navItems = [];

  toggleMenu() {
    this.layoutService.toggleMobileSidebar();
  }

  toggleProfile() {
    this.isProfileOpen.set(!this.isProfileOpen());
    if (!this.isProfileOpen()) {
      this.isEditingProfile.set(false);
    }
  }

  startEditProfile() {
    this.draftProfileName.set(this.profileName());
    this.isEditingProfile.set(true);
  }

  onProfileNameInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.draftProfileName.set(target.value);
  }

/**
 * Saves the profile name to the API and local storage.
 *
 * If the profile name is empty, it will default to 'Sub Admin' or 'Admin' depending on the user's role.
 * If the API call is successful, it will update the profile name in local storage and show a success toast.
 * If the API call fails, it will show an error toast.
 */
  saveProfileName() {
    if (this.isSavingProfileName()) return;
    const trimmed = this.draftProfileName().trim();
    const fallbackName = this.isSubAdmin() ? 'Sub Admin' : 'Admin';
    const nextName = trimmed || fallbackName;
    const request$ = this.isSubAdmin()
      ? this.authService.updateSubAdminName(nextName)
      : this.authService.updateAdminName(nextName);

    this.isSavingProfileName.set(true);
    request$
      .pipe(finalize(() => this.isSavingProfileName.set(false)))
      .subscribe({
      next: (response: any) => {
        const responseName = response?.data?.name ?? response?.name ?? nextName;
        const finalName = typeof responseName === 'string' && responseName.trim()
          ? responseName.trim()
          : nextName;
        this.profileName.set(finalName);
        if (this.isSubAdmin()) {
          this.localStorageService.storeData('subAdminProfileName', finalName);
        } else {
          this.localStorageService.storeData('adminProfileName', finalName);
        }
        this.isEditingProfile.set(false);
        this.toastrService.showToast('Profile name updated');
        this.isProfileOpen.set(false);
      },
      error: () => {
        this.toastrService.showToast('Failed to update profile name', 'error');
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.isProfileOpen()) return;
    const target = event.target as Node | null;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.isProfileOpen.set(false);
      this.isEditingProfile.set(false);
    }
  }

  /**
   * Copies the connected wallet address to the clipboard.
   */
  copyAddress() {
    this.clipboardService.copy(this.connectedWalletAddress(), 'Wallet Address');
  }

  /**
   * Logout from the application with confirmation.
   */
  async logout() {
    const confirmed = await this.toastrService.confirmLogout();
    if (confirmed) {
      this.authService.logout();
      this.appKitService.disconnectWallet();
    }
  }
}
